import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Switch, Tooltip, Typography, useMediaQuery, useTheme} from '@mui/material';
import PlacesAutocompleteInputMui from '../../placesAutocomplete/PlacesAutocompleteInputMui';
import useClickOutsideRef from '../../../hooks/useClickOutsideRef';
import SearchFilterAndSort from './SearchFilterAndSort/SearchFilterAndSort';
import {
  ApprovalSwitch,
  FilterBox,
  FilterCollapse,
  FilterOptionsContainer,
  PacInputWrapper,
  SearchBarContainer,
  SearchFormControlLabel,
  SearchInfoIcon,
} from "./styles";
import {PlacePrediction} from "../../placesAutocomplete/interfaces";
import {debounce} from "throttle-debounce";
import {SearchFormContainer} from "./SearchFormContainer";
import {SearchFilterState} from "./SearchFilterAndSort/SearchFilterState";
import {useScrollFilterCollapse} from "../../../hooks/search/useScrollFilterCollapse";

const tooltipStyles = {
  tooltip: {
    sx: {
      lineHeight: '1.5em'
    }
  }
}

interface SearchFormProps {
  onSearchSubmit: (pacItem: PlacePrediction | undefined) => void;
  onSearchValueChange?: (value: string) => void;
  onInstantApprovalChange: (isTrue: boolean) => void;
  onLiveLocationsChange: (isTrue: boolean) => void;
  searchValue: string;
  instantApprovalOnly: boolean;
  onlyLiveLocations: boolean;
  showAdminOnlyOptions: boolean;
  filterState: SearchFilterState;
  hideFilterControls?: boolean;
  isFixed?: boolean;
}

const SearchForm = (props: SearchFormProps) => {
  const {
    onSearchSubmit,
    onSearchValueChange,
    onInstantApprovalChange,
    onLiveLocationsChange,
    searchValue,
    instantApprovalOnly,
    onlyLiveLocations,
    showAdminOnlyOptions,
    filterState,
    hideFilterControls = false,
    isFixed = true,
  } = props;

  const [selectedPacItem, setSelectedPacItem] = useState<PlacePrediction | undefined>();
  const [showPac, setShowPac] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [filtersTransitioning, setFiltersTransitioning] = useState<boolean>(false);
  const [localSearchValue, setLocalSearchValue] = useState<string>(searchValue);

  const containerRef = useRef<HTMLDivElement>(null);

  // Sync local state when prop changes (e.g., from parent clear)
  useEffect(() => {
    setLocalSearchValue(searchValue);
  }, [searchValue]);

  // Create stable debounced function
  const debouncedOnSearchValueChange = useMemo(
    () => debounce(300, (value: string) => {
      onSearchValueChange?.(value);
    }),
    [onSearchValueChange]
  );

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalSearchValue(value); // Update immediately for typing
    debouncedOnSearchValueChange(value); // Notify parent after debounce
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {name, checked} = event.target;
    switch (name) {
      case 'instant_approval_only':
        onInstantApprovalChange(checked);
        break;
      case 'only_live_locations':
        onLiveLocationsChange(checked);
        break;
      default:
        break;
    }
  };

  const handleSelectPac = (pacItem: PlacePrediction | undefined) => {
    setSelectedPacItem(pacItem);
  };

  const handleClickPac = (pacItem: PlacePrediction) => {
    debouncedOnSearchValueChange.cancel();
    setLocalSearchValue(pacItem.description || '');
    setShowPac(false);
    onSearchSubmit(pacItem);
  };

  const handleCancelIconClick = () => {
    debouncedOnSearchValueChange.cancel();
    setLocalSearchValue('');
    setSelectedPacItem(undefined);
  };

  const handleOnSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPacItem?.place_id) return;
    debouncedOnSearchValueChange.cancel();
    if (selectedPacItem.description) {
      setLocalSearchValue(selectedPacItem.description);
    }
    setShowPac(false);
    onSearchSubmit(selectedPacItem);
  };

  const handleOnFocus = () => {
    setShowPac(true);
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
  };

  const handleFiltersIconClick = (_e: React.MouseEvent<HTMLButtonElement>) => {
    setShowPac(false);
    setFiltersTransitioning(true);
    setShowFilters(prevState => !prevState);
  }

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const {handleCollapseTransitionEnd} = useScrollFilterCollapse({
    isMobile,
    filtersTransitioning,
    setFiltersTransitioning,
    setShowFilters,
  });

  useClickOutsideRef(containerRef, () => setShowPac(false));

  const instantApprovalCaption = "Search by locations that offer immediate access to book spaces.";
  const liveLocationsCaption = "Search by locations that are currently live and available to book.";

  return (
    <SearchFormContainer isFixed={isFixed} onSubmit={handleOnSearchSubmit}>
      <SearchBarContainer container item ref={containerRef} onFocus={handleOnFocus}>
        <PacInputWrapper item>
          <PlacesAutocompleteInputMui
            onChange={handleSearchInputChange}
            name="pacSearch"
            value={localSearchValue}
            onCancelIconClick={handleCancelIconClick}
            onFiltersIconClick={handleFiltersIconClick}
            selectedPacItem={selectedPacItem}
            handleClickPac={handleClickPac}
            handleSelectPac={handleSelectPac}
            containerRef={containerRef}
            showPac={showPac}
            showFilters={showFilters}
            handleKeyUp={handleKeyUp}
          />
        </PacInputWrapper>
      </SearchBarContainer>
      <FilterCollapse
        in={showFilters}
        onEntered={handleCollapseTransitionEnd}
        onExited={handleCollapseTransitionEnd}
        role="region"
        aria-label="Search filters"
        aria-hidden={!showFilters}
      >
        <FilterOptionsContainer container item showAdminOnlyOptions={showAdminOnlyOptions} hideFilterControls={hideFilterControls}>
          <FilterBox item container>
            <SearchFormControlLabel
              label={<Typography variant='body2'>Instant Approval Only</Typography>}
              control={<ApprovalSwitch color="secondary" name="instant_approval_only" checked={instantApprovalOnly} onChange={handleCheckChange} />}
            />
            <Tooltip componentsProps={tooltipStyles} arrow title={instantApprovalCaption} >
              <SearchInfoIcon color="secondary" />
            </Tooltip>
          </FilterBox>
          {
            showAdminOnlyOptions && <FilterBox item container>
              <SearchFormControlLabel
                label={<Typography variant='body2'>Only Live Locations</Typography>}
                control={<Switch color="secondary" name="only_live_locations" checked={onlyLiveLocations} onChange={handleCheckChange} />}
              />
              <Tooltip componentsProps={tooltipStyles} arrow title={liveLocationsCaption}>
                <SearchInfoIcon color="secondary" />
              </Tooltip>
            </FilterBox>
          }
        </FilterOptionsContainer>
        { !hideFilterControls &&
          <SearchFilterAndSort
            {...filterState}
          />
        }
      </FilterCollapse>
    </SearchFormContainer>
  );
};

export default React.memo(SearchForm);

